import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instructionsApi } from "../api/instructions";
import type { GroupFormData, InstructionFormData } from "../types";

export const KEYS = {
  groups: ["instructions", "groups"] as const,
  list: (groupId?: string) => ["instructions", "list", groupId ?? "all"] as const,
  detail: (id: string) => ["instructions", "detail", id] as const,
  versions: (id: string) => ["instructions", "versions", id] as const,
  meta: ["instructions", "meta"] as const,
};

export function useInstructionGroups() {
  return useQuery({ queryKey: KEYS.groups, queryFn: instructionsApi.listGroups });
}

export function useInstructions(groupId?: string, enabled = true) {
  return useQuery({
    queryKey: KEYS.list(groupId),
    queryFn: () => instructionsApi.list(groupId),
    enabled,
  });
}

export function useInstructionDetail(id: string | null) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: () => instructionsApi.retrieve(id!),
    enabled: !!id,
  });
}

export function useInstructionVersions(id: string | null) {
  return useQuery({
    queryKey: KEYS.versions(id!),
    queryFn: () => instructionsApi.listVersions(id!),
    enabled: !!id,
  });
}

export function useAccessMeta() {
  return useQuery({
    queryKey: KEYS.meta,
    queryFn: instructionsApi.getMeta,
    staleTime: Infinity,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: GroupFormData) => instructionsApi.createGroup(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.groups }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GroupFormData> }) =>
      instructionsApi.updateGroup(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.groups }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => instructionsApi.deleteGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.groups }),
  });
}

export function useCreateInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InstructionFormData) => instructionsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instructions", "list"] }),
  });
}

export function useUpdateInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InstructionFormData> }) =>
      instructionsApi.update(id, data),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: ["instructions", "list"] });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
    },
  });
}

export function useDeleteInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => instructionsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instructions", "list"] }),
  });
}

export function useDeleteAllInstructions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const all = await instructionsApi.list();
      await Promise.all(all.map((i) => instructionsApi.delete(i.id)));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["instructions"] });
    },
  });
}

export function useUploadVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, comment }: { id: string; file: File; comment: string }) =>
      instructionsApi.uploadVersion(id, file, comment),
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: KEYS.versions(id) });
      qc.invalidateQueries({ queryKey: KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: ["instructions", "list"] });
    },
  });
}
